import { sentry } from '@frontend/tracking';

export type LogErrorContext = {
  /** Defaults to `'Custom Context'` if omitted. */
  name?: string;
  data: Record<string, unknown>;
};

const buildContext = (context?: LogErrorContext) =>
  context ? { name: context.name ?? 'Custom Context', context: context.data } : undefined;

/**
 * Logs an error to the console and to Sentry under the `phone-config` topic.
 *
 * @param error - A descriptive message string or an `Error` instance (preserves stack trace).
 * @param context - Optional Sentry context payload. `name` defaults to `'Custom Context'`.
 *
 * @example
 * logError('Failed to fetch call routes');
 * logError(err, { name: 'Fetch Call Routes', data: { tenantId } });
 */
export const logError = (error: string | Error, context?: LogErrorContext) => {
  console.error(error);
  sentry.error({
    error,
    topic: 'phone-config',
    team: 'fe-phone-config',
    severityLevel: 'error',
    addContext: buildContext(context),
  });
};

/**
 * Logs a warning to the console and to Sentry under the `phone-config` topic.
 *
 * @param error - A descriptive message string or an `Error` instance.
 * @param context - Optional Sentry context payload. `name` defaults to `'Custom Context'`.
 *
 * @example
 * logWarning('Device list empty, falling back to default');
 * logWarning(err, { name: 'Device List', data: { tenantId } });
 */
export const logWarning = (error: string | Error, context?: LogErrorContext) => {
  console.warn(error);
  sentry.warn({
    error,
    topic: 'phone-config',
    team: 'fe-phone-config',
    addContext: buildContext(context),
  });
};
